/**
 * Changes CSS classes if an element is clicked. The configuration is based solely on data attributes.
 *
 * @module ClassClick
 * @author EN GARDE (Martin Holler, Marcel Seelig)
 * @version 1.7
 *
 * change log:
 * v1.7 send custom event data on changeClass
 * v1.6 use _.defaultsDeep instead of $.extend for settings object
 * v1.5 added outsider functionality
 * v1.4 added no-prevent to preserve original event
 * v1.3 added shortcodes that include selectors (e.g. "& .my-element", "< .my-element", "~ .my-element", …), added ">", "+", "~" shortcodes, removed "<<", "<<<" shortcodes
 * v1.2 added "<<", "<<<" & "+" shortcode
 * v1.1 added locked state & events
 * v1.0 initial release
 *
 * Available data attributes:
 * - [data-en-cc-target]: Element selector of the element(s) to change classes on. Supports the following special selectors: & (self), < (parent), << (parentParent), <<< (parentParentParent), + (next)
 * - [data-en-cc-class] (optional): Class name(s) to be changed (defaults to "en-is-active").
 * - [data-en-cc-method] (optional): Whether to add, remove or toggle the class (defaults to "toggle").
 * - [data-en-cc-highlander] (optional): If true, the class will also be removed from sibling elements.
 * - [data-en-cc-cancel] (optional): Prevents execution of classClick methods on all parents (see example below).
 * - [data-en-cc-no-prevent] (optional): Do not prevent default event execution.
 *
 * Examples:
 * <!-- toggles an element's class -->
 * <button data-en-cc-target=".en-menu" data-en-cc-class="en-menu--open">Toggle menu</button>
 *
 * <!-- toggles multiple elements' classes -->
 * <button data-en-cc-target=".en-menu|.en-sidebar,body" data-en-cc-class="en-menu--open|en-menu-is-open">Toggle menu and inform others</button>
 *
 * <!-- adds a class to one element and removes it from another -->
 * <button data-en-cc-target=".en-menu|.en-sidebar" data-en-cc-class="en-menu--open|en-sidebar--open" data-en-cc-method="add|remove">Add and remove</button>
 *
 * <!-- toggle self and parent element's class -->
 * <div><button data-en-cc-target="&|<">Toggle self and parent</button></div>
 *
 * <!-- adds a class to list items and only allows one to be active at a time -->
 * <ul>
 *     <li data-en-cc-target="&" data-en-cc-method="add" data-en-cc-highlander>First Item</li>
 *     <li data-en-cc-target="&" data-en-cc-method="add" data-en-cc-highlander>Second Item</li>
 *     <li data-en-cc-target="&" data-en-cc-method="add" data-en-cc-highlander>Third Item</li>
 * </ul>
 *
 * <!-- prevent event bubbling on an element -->
 * <div class="en-modal en-is-active" data-en-cc-target="&" data-en-cc-method="remove">
 *     <!-- the modal will only be closed when the area around the inner element is clicked -->
 *     <div class="en-modal__inner" data-en-cc-cancel>Some Text</div>
 * </div>
 *
 * <!-- do not prevent original event -->
 * <a href="#target" data-en-cc-target="#target" data-en-cc-no-prevent></a>
 * <!-- browser will still change hash AND trigger classClick -->
 *
 * <!-- remove a class from an element if anything outside it is clicked -->
 * <!-- note that this requires the "checkOutsiders" option to be set to true -->
 * <div class="en-menu" data-en-cc-outsider="en-is-open">
 *     <button class="en-menu__trigger" data-en-cc-target="<" data-en-cc-class="en-is-open">Open</button>
 * </div>
 */

import $ from 'jquery';
import defaultsDeep from 'lodash/defaultsDeep';

const ClassClick = ( function() {
	'use strict';

	var options = {
		shortcodes: {
			self: '&',
			parent: '<',
			child: '>',
			next: '+',
			siblings: '~'
		},
		attributes: {
			target: 'data-en-cc-target',
			className: 'data-en-cc-class',
			method: 'data-en-cc-method',
			highlander: 'data-en-cc-highlander',
			cancel: 'data-en-cc-cancel',
			noPrevent: 'data-en-cc-no-prevent',
			outsider: 'data-en-cc-outsider'
		},
		classNames: {
			active: 'en-is-active',
			locked: 'en-is-locked'
		},
		checkOutsiders: false
	};

	/**
   * Fail-safe string split function. Always returns an array (empty if the given string is invalid).
   * @param {string} string
   * @param {string} separator
   * @return {array}
   */
	var splitString = function( string, separator ) {
		if ( typeof string === 'string' ) {
			return string.split( separator );
		}
		else {
			return [];
		}
	};

	/**
   * Adds, removes or toggles an element's (or a set of elements') class.
   * @param {jQuery} $elements
   * @param {string} [className=en-is-active]
   * @param {string} [method=toggle] - Possible values: add, remove or toggle.
   * @param {boolean} [highlander=false] - If true, this removes the class from the element's siblings.
   * @return {jQuery}
   */
	var changeClass = function( $elements, className, method, highlander ) {
		className = className || options.classNames.active;
		highlander = highlander !== undefined || false;

		$elements.each( function() {
			var $element = $( this );

			// remove the class from siblings if highlander is true
			if ( highlander && $elements.length === 1 && method !== 'remove' ) {
				$element.siblings().removeClass( className );
			}

			// custom data when firing add/remove/toggle event
			var data = {
				'$element': $element,
				'$elements': $elements,
				'className': className,
				'method': method,
				'highlander': highlander
			};

			switch ( method ) {
				case 'add':
					$element.addClass( className ).trigger( 'classclickadd', data );
					break;
				case 'remove':
					$element.removeClass( className ).trigger( 'classclickremove', data );
					break;
				default:
					$element.toggleClass( className ).trigger( $element.hasClass( className ) ? 'classclickadd' : 'classclickremove', data );
					break;
			}
		} );

		return $elements;
	};

	/**
   * Merges options and sets up event listeners.
   * @memberof ClassClick
   * @param {object} [settings] - Overrides options, see below.
   * @property {object} shortcodes - Collection of selector shortcodes.
   * @property {object} attributes - Collection of data attribute names used to select elements.
   * @property {object} classNames - Collection of class names that are set and read.
   * @property {boolean} checkOutsiders=false - Whether or not to check for outsiders (see example in head).
   */
	var init = function( settings ) {
		options = defaultsDeep( settings, options );

		$( document ).on( 'click', '[' + options.attributes.cancel + ']', function( e ) {
			e.classClickCancel = true;
		} );

		$( document ).on( 'click', '[' + options.attributes.target + ']', function( e ) {
			// bail if the event was canceled
			if ( e.classClickCancel ) {
				return;
			}

			var $this = $( this );
			var targets = splitString( $this.attr( options.attributes.target ), '|' );
			var classNames = splitString( $this.attr( options.attributes.className ), '|' );
			var methods = splitString( $this.attr( options.attributes.method ), '|' );
			var highlanders = splitString( $this.attr( options.attributes.highlander ), '|' );
			var noPrevent = $this.attr( options.attributes.noPrevent ) !== undefined && $this.attr( options.attributes.noPrevent ) !== false;

			if ( !noPrevent ) {
				e.preventDefault();
			}

			// bail if locked
			if ( $this.hasClass( options.classNames.locked ) ) {
				return;
			}

			$.each( targets, function( index, target ) {
				var $target;
				var split = target.split( / (.+)?/ );
				var shortcode = split[0];
				var selector = split[1];

				switch ( shortcode ) {
					case options.shortcodes.self:
						$target = selector ? $this.find( selector ) : $this;
						break;
					case options.shortcodes.parent:
						$target = selector ? $this.parent().closest( selector ) : $this.parent();
						break;
					case options.shortcodes.child:
						$target = $this.children( selector );
						break;
					case options.shortcodes.next:
						$target = $this.next( selector );
						break;
					case options.shortcodes.siblings:
						$target = $this.siblings( selector );
						break;
					default:
						$target = $( target );
				}

				changeClass( $target, classNames[index], methods[index], highlanders[index] );
			} );
		} );

		if ( options.checkOutsiders ) {
			$( document ).on( 'click', function( e ) {
				var $openedOutsiders = $( '[' + options.attributes.outsider + ']' ).filter( function() {
					var $this = $( this );

					return $this.hasClass( $this.attr( options.attributes.outsider ) );
				} );

				if ( $openedOutsiders.length && !$( e.target ).closest( $openedOutsiders ).length ) {
					$openedOutsiders.each( function() {
						var $this = $( this );

						changeClass( $this, $this.attr( options.attributes.outsider ), 'remove' );
					} );
				}
			} );
		}
	};

	// exports
	return {
		init: init
	};

} )();

export default ClassClick;
